import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminAPI from '../../api/adminApi';
import VideoPlayer from '../../components/VideoPlayer';
import ImageGallery from '../../components/ImageGallery';
import logo from '../../images/laundry-care-logo.png';
import {
  agreementFields,
  qualificationFields,
  workExperienceFields,
} from '../../api/applicationData';
import StatusTag from '../../components/StatusTag';

const AdminApplicantDetail = () => {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const [applicant, setApplicant] = React.useState(null);
  const [media, setMedia] = React.useState({
    workspaceImages: [],
    foldedItemsImages: [],
    driversLicenseImages: [],
    washingMachineLabelImages: [],
    videos: [],
  });
  const [loading, setLoading] = React.useState(true);
  const [updating, setUpdating] = React.useState(false);
  const [selectedStatus, setSelectedStatus] = React.useState('');

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = React.useState({
    personalInfo: true,
    qualifications: false,
    workExperience: false,
    availability: false,
    agreements: false,
    mediaUploads: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const statusOptions = [
    { value: 'applicationStarted', label: 'Application Started' },
    { value: 'applicationSubmitted', label: 'Application Submitted' },
    { value: 'activated', label: 'Activated' },
    { value: 'review', label: 'Review' },
    { value: 'interview', label: 'Interview' },
    { value: 'interviewReview', label: 'Interview Review' },
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'locationRejected', label: 'Location Rejected' },
    { value: 'applicantRejected', label: 'Applicant Rejected' },
    { value: 'expired', label: 'Expired' },
  ];

  React.useEffect(() => {
    loadApplicantData();
  }, [applicantId]);

  const loadApplicantData = async () => {
    setLoading(true);
    try {
      const applicantData = await AdminAPI.getApplicantById(applicantId);
      setApplicant(applicantData);
      setSelectedStatus(applicantData.applicationStatus || 'pending');

      // Extract media files from mediaData component
      const workspaceImages = [];
      const foldedItemsImages = [];
      const driversLicenseImages = [];
      const washingMachineLabelImages = [];
      const videos = [];
      const STRAPI_URL =
        import.meta.env.VITE_STRAPI_URL || 'http://localhost:1337';

      // If mediaData exists on the applicant, extract media URLs
      if (applicantData.mediaData) {
        const mediaData = applicantData.mediaData;

        // Helper function to process media array or single object
        const processMediaArray = (mediaArray, title, targetArray) => {
          if (!mediaArray) return;

          // Handle both single object and array
          const items = Array.isArray(mediaArray) ? mediaArray : [mediaArray];

          items.forEach((item, idx) => {
            // Handle both direct media objects and nested structures
            let url = item?.url || item?.attributes?.url;
            // Prepend Strapi URL if it's a relative path
            if (url && !url.startsWith('http')) {
              url = `${STRAPI_URL}${url}`;
            }
            if (url) {
              targetArray.push({
                id: `${title.toLowerCase().replace(/\s+/g, '-')}-${idx}`,
                url: url,
                title: title,
              });
            }
          });
        };

        // Handle workspace images
        processMediaArray(
          mediaData.workspaceImages,
          'Workspace Image',
          workspaceImages
        );

        // Handle folded items images
        processMediaArray(
          mediaData.foldedItemsImages,
          'Folded Items Image',
          foldedItemsImages
        );

        // Handle driver's license
        processMediaArray(
          mediaData.driversLicense,
          "Driver's License",
          driversLicenseImages
        );

        // Handle washing machine label
        processMediaArray(
          mediaData.washingMachineLabel,
          'Washing Machine Label',
          washingMachineLabelImages
        );

        // Handle intro video
        if (mediaData.introVideo) {
          // Handle both single object and array
          const videoItems = Array.isArray(mediaData.introVideo)
            ? mediaData.introVideo
            : [mediaData.introVideo];

          videoItems.forEach((item, idx) => {
            let url = item?.url || item?.attributes?.url;
            // Prepend Strapi URL if it's a relative path
            if (url && !url.startsWith('http')) {
              url = `${STRAPI_URL}${url}`;
            }
            if (url) {
              videos.push({
                id: `intro-${idx}`,
                url: url,
                title: 'Introduction Video',
              });
            }
          });
        }
      }

      setMedia({
        workspaceImages,
        foldedItemsImages,
        driversLicenseImages,
        washingMachineLabelImages,
        videos,
      });
    } catch (error) {
      console.error('Error loading applicant data:', error);
      alert('Failed to load applicant details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;

    setUpdating(true);
    try {
      // Check if status is being changed to expired (which triggers media deletion)
      const shouldDeleteMedia =
        selectedStatus === 'expired' ||
        selectedStatus === 'locationRejected' ||
        selectedStatus === 'applicantRejected';

      if (shouldDeleteMedia) {
        const confirmDelete = window.confirm(
          `Changing status to "expired" will permanently delete all uploaded images and videos from storage. Continue?`
        );

        if (!confirmDelete) {
          setUpdating(false);
          return;
        }

        // Delete all media files from Strapi storage
        try {
          const result = await AdminAPI.deleteAllApplicantMedia(applicant);

          // Check if any files failed to delete
          if (result.failedFiles && result.failedFiles.length > 0) {
            console.error('Some files failed to delete:', result.failedFiles);
            const failureMessage = result.failedFiles
              .map((f) => f.error?.message || JSON.stringify(f.error))
              .join('\n');
            alert(
              `Warning: Failed to delete ${result.failedFiles.length} file(s).\n\nDetails:\n${failureMessage}\n\nPlease check Strapi permissions.`
            );
            setUpdating(false);
            return;
          }

          console.log(
            `✅ Successfully deleted ${result.deletedFiles.length} file(s)`
          );
        } catch (error) {
          console.error('Error deleting media files:', error);
          alert(error.message || 'Failed to delete media files.');
          setUpdating(false);
          return;
        }
      }

      // Update the status
      await AdminAPI.updateApplicantStatus(
        applicant.documentId,
        selectedStatus
      );

      alert(
        shouldDeleteMedia
          ? 'Status updated and all media files deleted successfully!'
          : 'Status updated successfully'
      );
      loadApplicantData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      applicationStarted: 'bg-blue-100 text-blue-800',
      applicationSubmitted: 'bg-indigo-100 text-indigo-800',
      activated: 'bg-green-100 text-green-800',
      review: 'bg-purple-100 text-purple-800',
      interview: 'bg-yellow-100 text-yellow-800',
      interviewReview: 'bg-amber-100 text-amber-800',
      onboarding: 'bg-teal-100 text-teal-800',
      locationRejected: 'bg-orange-100 text-orange-800',
      applicantRejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading applicant details...</p>
        </div>
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Applicant not found</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-blue-500 hover:text-blue-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Applicant Details
              </h1>
            </div>
            <img src={logo} alt="Laundry Care" className="h-12" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Management */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Update Status</h2>
          <div className="flex max-w-md gap-4">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleStatusUpdate}
              disabled={
                updating || selectedStatus === applicant.applicationStatus
              }
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {updating ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>

        {/* Application Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 space-y-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-3">
            Application Details
          </h2>

          {/* Personal Info Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('personalInfo')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                Personal Information
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.personalInfo ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.personalInfo && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Full Name</p>
                  <p className="font-semibold text-gray-800">
                    {applicant?.personalInfo?.fullName || '—'}
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Date of Birth</p>
                  <p className="font-semibold text-gray-800">
                    {applicant?.personalInfo?.dob || '—'}
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Phone Number</p>
                  <p className="font-semibold text-gray-800">
                    {applicant?.personalInfo?.phone_number || '—'}
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Email Address</p>
                  <p className="font-semibold text-gray-800 break-words">
                    {applicant?.email || '—'}
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Referral Code</p>
                  <p className="font-semibold text-gray-800">
                    {applicant?.personalInfo?.referral_code || 'None'}
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">
                    Application Status
                  </p>
                  {/* <span
                    className={`inline-block px-3 py-1 mt-1 text-sm font-semibold rounded-full ${getStatusColor(
                      applicant.applicationStatus
                    )}`}>
                    {applicant.applicationStatus || 'pending'}
                  </span> */}
                  <StatusTag
                    status={
                      applicant?.applicationStatus
                        ? applicant?.applicationStatus
                        : null
                    }
                  />
                </div>
                <div className="bg-white p-4 rounded border border-gray-200 md:col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Address</p>
                  <p className="font-semibold text-gray-800">
                    {applicant?.personalInfo?.address_line_1}
                    {applicant?.personalInfo?.address_line_2 &&
                      `, ${applicant?.personalInfo?.address_line_2}`}
                    <br />
                    {applicant?.personalInfo?.city},{' '}
                    {applicant?.personalInfo?.state}{' '}
                    {applicant?.personalInfo?.postal_code}
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Qualifications Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('qualifications')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
                Qualifications
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.qualifications ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.qualifications && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3  mt-4">
                {qualificationFields.map(({ key, label }) => (
                  <div
                    key={key}
                    className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-700">{label}</p>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        applicant?.qualifications?.[key]
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                      {applicant?.qualifications?.[key] ? 'Yes' : 'No'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Work Experience Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('workExperience')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
                </svg>
                Work Experience
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.workExperience ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.workExperience && (
              <div className="space-y-3  mt-4">
                {workExperienceFields.map(({ key, label }) => (
                  <div
                    key={key}
                    className="bg-white p-4 rounded border border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">{label}</p>
                    <p className="text-gray-800">
                      {applicant?.experience?.[key] || '—'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Availability Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('availability')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                </svg>
                Availability
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.availability ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.availability && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {applicant?.availability?.map((dayBlock) => {
                  const times = ['morning', 'afternoon', 'evening', 'none']
                    .filter((time) => dayBlock[time])
                    .map((time) => time.charAt(0).toUpperCase() + time.slice(1))
                    .join(', ');
                  return (
                    <div
                      key={dayBlock.day}
                      className="bg-white p-4 rounded border border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 capitalize mb-2">
                        {dayBlock.day}
                      </p>
                      <p className="text-sm text-gray-800">
                        {times || 'Not Available'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Agreements Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('agreements')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                Agreements & Acknowledgments
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.agreements ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.agreements && (
              <div className="space-y-3 mt-4">
                {agreementFields.map(({ key, statement }) => (
                  <div
                    key={key}
                    className="bg-white p-4 rounded border border-gray-200 flex items-start">
                    <div className="flex-shrink-0 mr-3 mt-1">
                      {applicant?.agreements?.[key] ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6 text-green-600"
                          viewBox="0 0 24 24"
                          fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-6 h-6 text-gray-400"
                          viewBox="0 0 24 24"
                          fill="currentColor">
                          <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{statement}</p>
                      <span
                        className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          applicant?.agreements?.[key]
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                        {applicant?.agreements?.[key] ? 'Agreed' : 'Not Agreed'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Media Uploads Section */}
          <section className="bg-[#f7f8fb] p-6 rounded-lg">
            <h3
              className="text-xl font-semibold text-gray-900 flex items-center justify-between cursor-pointer"
              onClick={() => toggleSection('mediaUploads')}>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 mr-2"
                  fill="currentColor">
                  <path d="M18 15v3H6v-3H4v3c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3h-2zM7 9l1.41 1.41L11 7.83V16h2V7.83l2.59 2.58L17 9l-5-5-5 5z" />
                </svg>
                Media Uploads
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`w-6 h-6 transition-transform ${
                  expandedSections.mediaUploads ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </h3>
            {expandedSections.mediaUploads && (
              <div className="space-y-6 mt-4">
                {/* Videos */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5 mr-2"
                      fill="currentColor">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                    </svg>
                    Videos
                  </h4>
                  {media.videos.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-500">No videos uploaded yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {media.videos.map((video) => (
                        <VideoPlayer
                          key={video.id}
                          videoUrl={video.url}
                          title={video.title}
                          mediaId={video.id}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Images */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5 mr-2"
                      fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                    Images
                  </h4>
                  <div className="space-y-6">
                    {/* Workspace Images */}
                    <div>
                      <h5 className="text-md font-semibold text-gray-700 mb-3">
                        Workspace Images
                      </h5>
                      {media.workspaceImages.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <p className="text-gray-500">
                            No workspace images uploaded
                          </p>
                        </div>
                      ) : (
                        <ImageGallery images={media.workspaceImages} />
                      )}
                    </div>

                    {/* Folded Items Images */}
                    <div>
                      <h5 className="text-md font-semibold text-gray-700 mb-3">
                        Folded Items Images
                      </h5>
                      {media.foldedItemsImages.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <p className="text-gray-500">
                            No folded items images uploaded
                          </p>
                        </div>
                      ) : (
                        <ImageGallery images={media.foldedItemsImages} />
                      )}
                    </div>

                    {/* Driver's License Images */}
                    <div>
                      <h5 className="text-md font-semibold text-gray-700 mb-3">
                        Driver's License
                      </h5>
                      {media.driversLicenseImages.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <p className="text-gray-500">
                            No driver's license images uploaded
                          </p>
                        </div>
                      ) : (
                        <ImageGallery images={media.driversLicenseImages} />
                      )}
                    </div>

                    {/* Washing Machine Label Images */}
                    <div>
                      <h5 className="text-md font-semibold text-gray-700 mb-3">
                        Washing Machine Label
                      </h5>
                      {media.washingMachineLabelImages.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 text-center">
                          <p className="text-gray-500">
                            No washing machine label images uploaded
                          </p>
                        </div>
                      ) : (
                        <ImageGallery
                          images={media.washingMachineLabelImages}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminApplicantDetail;
